import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { take } from 'rxjs/operators';
import { environment as env } from 'src/environments/environment';
import { SidetrayDataService } from '../sidetray/sidetray-data.service';
import { CcAdminApiHelperService } from '../ccAdmin/cc-admin-api-helper.service';
import { CcAdminApiService } from '../ccAdmin/cc-admin-api.service';
import { Observable, Subject } from 'rxjs';
import { RuntimeDataTestWorkflowUpdate } from 'src/app/interfaces/runtime-data.interface';

@Injectable({
	providedIn: 'root',
})
export class SignalRService {
	private hubConnection: HubConnection;

	public get connectionId() {
		return this.hubConnection.connectionId;
	}

	/** Observable to track Workspace disable trigger events */
	private workspaceDisableTriggeredSubject: Subject<string> = new Subject<string>();
	public get workspaceDisableTriggered$(): Observable<string> {
		return this.workspaceDisableTriggeredSubject.asObservable();
	}
	public set workspaceDisableTriggered(workspaceId: string) {
		this.workspaceDisableTriggeredSubject.next(workspaceId);
	}

	/** Observable to track newly created/updated runtime data entries */
	private runtimeDataEntryCreatedSubject: Subject<string> = new Subject<string>();
	public get runtimeDataEntryCreated$(): Observable<string> {
		return this.runtimeDataEntryCreatedSubject.asObservable();
	}
	private set runtimeDataEntryCreated(runtimeDataEntryId: string) {
		this.runtimeDataEntryCreatedSubject.next(runtimeDataEntryId);
	}

	/** Observable of runtime data updates for test workflow */
	private readonly runtimeDataUpdatesSubject: Subject<RuntimeDataTestWorkflowUpdate> =
		new Subject<RuntimeDataTestWorkflowUpdate>();

	public get runtimeDataUpdates$(): Observable<RuntimeDataTestWorkflowUpdate> {
		return this.runtimeDataUpdatesSubject.asObservable();
	}

	constructor(
		private sidetrayDataService: SidetrayDataService,
		private ccAdminApiHelperService: CcAdminApiHelperService,
		private ccAdminApiService: CcAdminApiService
	) {
		this.ccAdminApiService.graphInitialized$.pipe(take(1)).subscribe(() => {
			this.signalRInit();
		});
	}

	private signalRInit() {
		this.ccAdminApiHelperService.getUserInfo().then(userinfo => {
			const agencyId = userinfo.agency.id;
			this.hubConnection = new HubConnectionBuilder()
				.withUrl(`${env.manageApiUrl}`, {
					withCredentials: false,
					accessTokenFactory: () => this.ccAdminApiHelperService.getToken(),
					headers: {
						agencyId,
					},
				})
				.withAutomaticReconnect()
				.configureLogging(LogLevel.None)
				.build();

			this.hubConnection.onreconnected(() => {
				// Refresh RDT on reconnection as some data might have been missed
				this.runtimeDataEntryCreated = 'reloadData';
			});

			this.hubConnection.start().catch(err => console.error(err.toString()));
			this.hubConnection.on('testworkflow', runtimeObj => {
				this.runtimeDataUpdatesSubject.next({
					runtimeData: runtimeObj,
				});
			});
			this.hubConnection.on('reloadNodes', () => this.sidetrayDataService.reloadSidetray());
			// SignalR message from Egress to start/edit/stop a Workspace disable timer. The data should contain the workspaceId.
			this.hubConnection.on('WorkspaceDisableTimerTriggered', data => {
				this.workspaceDisableTriggeredSubject.next(data.workspaceId);
			});
			// SignalR message from Egress to query for a runtime data table entity on Workflow trigger
			this.hubConnection.on('runtimeDataEntryCreation', data => {
				this.runtimeDataEntryCreated = data.runtimeEntryId;
			});
		});
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
